import React from 'react';
import { useQuery } from 'react-query';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Phone as PhoneIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { api } from '../services/api';

const SERVICE_STATUS = {
  healthy: {
    label: 'Operational',
    color: 'success',
    icon: CheckCircleIcon,
  },
  disconnected: {
    label: 'Disconnected',
    color: 'error',
    icon: ErrorIcon,
  },
  unreachable: {
    label: 'Unreachable',
    color: 'error',
    icon: ErrorIcon,
  },
  unconfigured: {
    label: 'Not configured',
    color: 'warning',
    icon: WarningIcon,
  },
  error: {
    label: 'Error',
    color: 'error',
    icon: ErrorIcon,
  },
  unhealthy: {
    label: 'Unhealthy',
    color: 'error',
    icon: ErrorIcon,
  },
  degraded: {
    label: 'Degraded',
    color: 'warning',
    icon: WarningIcon,
  },
  unknown: {
    label: 'Unknown',
    color: 'default',
    icon: WarningIcon,
  },
};

const SERVICE_LABELS = [
  { key: 'database', label: 'Database' },
  { key: 'redis', label: 'Redis Cache' },
  { key: 'ipqs', label: 'IPQualityScore API' },
  { key: 'rhino', label: 'Rhino TAS Service' },
  { key: 'speechToText', label: 'STT Service' },
  { key: 'translation', label: 'Translator Service' },
  { key: 'textToSpeech', label: 'TTS Service' },
];

function StatCard({ title, value, icon, color }) {
  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4">
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: color,
              borderRadius: '50%',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function StatusRow({ label, status }) {
  const statusConfig = SERVICE_STATUS[status] || SERVICE_STATUS.unknown;
  const StatusIcon = statusConfig.icon;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(220px, 1fr) 160px' },
        alignItems: 'center',
        gap: 1.5,
        py: 1.25,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <StatusIcon color={statusConfig.color} sx={{ mr: 1.5, fontSize: 22, flex: '0 0 auto' }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
      </Box>
      <Chip
        label={statusConfig.label}
        size="small"
        color={statusConfig.color}
        variant={status === 'healthy' ? 'filled' : 'outlined'}
        sx={{
          justifySelf: { xs: 'start', sm: 'end' },
          minWidth: 118,
          fontWeight: 700,
        }}
      />
    </Box>
  );
}

const formatPercent = (used, total) => {
  if (!total || total <= 0) {
    return 0;
  }
  return Math.min(100, Math.round((used / total) * 100));
};

const formatRatio = (ratio) => Number.isFinite(ratio) ? `${Math.round(ratio * 100)}%` : 'N/A';

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return 'N/A';
  }
  if (bytes >= 1024 ** 3) {
    return `${(bytes / (1024 ** 3)).toFixed(1)} GB`;
  }
  return `${Math.round(bytes / (1024 ** 2))} MB`;
};

function CapacityBar({ label, used, total, color = 'primary' }) {
  const percent = formatPercent(used, total);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>
          {label}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {used || 0}/{total || 0}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percent}
        color={color}
        sx={{ height: 8, borderRadius: 4 }}
      />
    </Box>
  );
}

function MediaWorkerUsage({ status, onRefresh, isRefreshing }) {
  const pool = status?.pool || {};
  const workers = status?.workers || [];

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Media Worker Groups
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Each group combines media-gateway + ari-controller for AI1 and AI2 calls.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip size="small" label={`${pool.mediaServers || 0} hosts`} />
          <Chip size="small" label={`${pool.readyWorkers || 0}/${pool.mediaWorkers || 0} groups ready`} color={pool.readyWorkers > 0 ? 'success' : 'warning'} />
          {pool.drainingWorkers > 0 && <Chip size="small" label={`${pool.drainingWorkers} draining`} color="warning" variant="outlined" />}
          {pool.unhealthyWorkers > 0 && <Chip size="small" label={`${pool.unhealthyWorkers} unhealthy`} color="error" variant="outlined" />}
          {pool.resourcePressureWorkers > 0 && <Chip size="small" label={`${pool.resourcePressureWorkers} resource pressure`} color="warning" variant="outlined" />}
          <Chip
            size="small"
            label={pool.resourceAdmissionEnforcedWorkers > 0 ? 'Admission enforced' : 'Admission monitoring'}
            color={pool.resourceAdmissionEnforcedWorkers > 0 ? 'warning' : 'default'}
            variant="outlined"
          />
          <Tooltip title="Refresh media worker health">
            <span>
              <IconButton size="small" onClick={onRefresh} disabled={isRefreshing}>
                <RefreshIcon fontSize="small" sx={{ animation: isRefreshing ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={2}>
          <CapacityBar label="Active Calls" used={pool.activeCalls} total={pool.maxActiveCalls} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <CapacityBar label="AI1 Calls" used={pool.activeAI1Calls} total={pool.maxActiveCalls} color="info" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <CapacityBar label="AI2 Calls" used={pool.activeTranslations} total={pool.maxTranslations} color="secondary" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <CapacityBar label="AI2 Stream Slots" used={pool.providerStreamsUsed} total={pool.maxProviderStreams} color="secondary" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <CapacityBar
            label="Gateway RTP"
            used={(pool.rtpPortsTotal || 0) - (pool.rtpPortsFree || 0)}
            total={pool.rtpPortsTotal}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <CapacityBar
            label="Controller RTP"
            used={(pool.externalRtpPortsTotal || 0) - (pool.externalRtpPortsFree || 0)}
            total={pool.externalRtpPortsTotal}
            color="success"
          />
        </Grid>
      </Grid>

      <Divider />

      <Box sx={{ py: 1 }}>
        {workers.length === 0 && (
          <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
            No media workers are registered.
          </Typography>
        )}
        {workers.map((worker, index) => (
          <React.Fragment key={worker.workerId}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: '1.55fr repeat(5, minmax(100px, 1fr)) 120px',
                },
                gap: 2,
                py: 1.5,
                alignItems: 'center',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {worker.workerId}
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block">
                  media-gateway + ari-controller
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block">
                  Host: {worker.mediaServerId} - {worker.region}/{worker.zone}
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block">
                  CPU {formatRatio(worker.resourceAdmission?.sample?.cpu?.ewma30sRatio)}
                  {' | '}Memory {formatBytes(worker.resourceAdmission?.sample?.memory?.usedBytes)} ({formatRatio(worker.resourceAdmission?.sample?.memory?.ratio)})
                  {' | '}Network {formatRatio(worker.resourceAdmission?.sample?.network?.ratio)}
                </Typography>
              </Box>
              <CapacityBar label="Calls" used={worker.activeCalls} total={worker.maxActiveCalls} />
              <CapacityBar label="AI1 Calls" used={worker.activeAI1Calls} total={worker.maxActiveCalls} color="info" />
              <CapacityBar label="AI2 Calls" used={worker.activeTranslations} total={worker.maxTranslations} color="secondary" />
              <CapacityBar label="AI2 Slots" used={worker.providerStreamsUsed} total={worker.maxProviderStreams} color="secondary" />
              <CapacityBar
                label="Gateway RTP"
                used={(worker.rtpPortsTotal || 0) - (worker.rtpPortsFree || 0)}
                total={worker.rtpPortsTotal}
                color="success"
              />
              <Chip
                size="small"
                label={worker.assignable ? 'Assignable' : (worker.lastNotAssignableReason || worker.status || 'Unavailable')}
                color={worker.assignable ? 'success' : 'warning'}
                variant={worker.assignable ? 'filled' : 'outlined'}
                sx={{ justifySelf: { xs: 'start', md: 'end' }, fontWeight: 700 }}
              />
            </Box>
            {index < workers.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Box>

      <Divider />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 2, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="textSecondary">
          Fail-open count last 5m: {pool.failOpenCountLast5m || 0}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Last updated: {status?.timestamp ? new Date(status.timestamp).toLocaleTimeString() : 'N/A'}
        </Typography>
      </Box>
    </Paper>
  );
}

export default function Dashboard() {
  const { data: stats, isLoading } = useQuery(
    'callStats',
    () => api.getCallStatistics('24h'),
    {
      refetchInterval: 30000,
    }
  );

  const { data: blockedCount } = useQuery(
    'blockedCount',
    async () => {
      const blocked = await api.getBlockedNumbers({ limit: 1, offset: 0 });
      return blocked.length;
    }
  );

  const { data: health } = useQuery(
    'health',
    () => api.getHealth(),
    {
      refetchInterval: 15000,
      retry: false,
    }
  );

  const { data: mediaWorkerStatus, refetch: refetchMediaWorkers, isFetching: isRefreshingWorkers } = useQuery(
    'mediaWorkerStatus',
    () => api.getMediaWorkerStatus(),
    {
      refetchInterval: 5000,
      retry: false,
    }
  );

  const overallStatus = health?.status || 'unknown';
  const overallConfig = overallStatus === 'healthy'
    ? { label: 'Healthy', color: 'success' }
    : overallStatus === 'degraded'
      ? { label: 'Degraded', color: 'warning' }
      : { label: 'Unknown', color: 'default' };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body1" color="textSecondary" paragraph>
        Real-time scammer detection statistics
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Calls (24h)"
            value={stats?.total_calls || 0}
            icon={<PhoneIcon />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Scammer Calls"
            value={stats?.scammer_calls || 0}
            icon={<WarningIcon />}
            color="error.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Blocked Numbers"
            value={blockedCount || 0}
            icon={<BlockIcon />}
            color="warning.main"
          />
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                gap: 2,
                mb: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  System Status
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Service availability and integration health
                </Typography>
              </Box>
              <Chip
                label={overallConfig.label}
                color={overallConfig.color}
                size="small"
                sx={{ fontWeight: 700, minWidth: 96 }}
              />
            </Box>

            <Divider />

            <Box sx={{ py: 1 }}>
              {SERVICE_LABELS.map((service, index) => (
                <React.Fragment key={service.key}>
                  <StatusRow
                    label={service.label}
                    status={health?.services?.[service.key] || 'unknown'}
                  />
                  {index < SERVICE_LABELS.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Typography variant="caption" color="textSecondary">
                Last updated: {health?.timestamp ? new Date(health.timestamp).toLocaleTimeString() : 'N/A'}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <MediaWorkerUsage status={mediaWorkerStatus} onRefresh={refetchMediaWorkers} isRefreshing={isRefreshingWorkers} />
        </Grid>
      </Grid>
    </Box>
  );
}
